// src/config/languages.js

// Full catalog for future rollout planning.
// App-language activation is controlled separately so we only expose
// languages that are ready for a credible healthcare experience.

export const LANGUAGES = [
  // Core (SADC + default)
  { code: "en-GB", label: "English (UK)" },
  { code: "en", label: "English" }, // compatibility alias
  { code: "tn", label: "Setswana" },
  { code: "zu", label: "isiZulu" },
  { code: "xh", label: "isiXhosa" },
  { code: "af", label: "Afrikaans" },
  { code: "sn", label: "Shona" },
  { code: "nd", label: "isiNdebele" },
  { code: "bem", label: "Bemba" },
  { code: "ny", label: "Nyanja / Chichewa" },
  { code: "pt", label: "Português" },

  // European languages (Diaspora + investor confidence + future EuroMed)
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },                // Standard FR-FR
  { code: "fr-CH", label: "Français (Suisse)" },    // Swiss French
  { code: "fr-CA", label: "Français (Canada)" },    // Québec-friendly
];

export const ACTIVATED_APP_LANGUAGES = [
  { code: "en-GB", label: "English" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
  { code: "sn", label: "Shona" },
  { code: "zu", label: "isiZulu" },
];

export const ACTIVATED_APP_LANGUAGE_CODES = ACTIVATED_APP_LANGUAGES.map(
  (language) => language.code,
);

// Alias for now (kept for compatibility)
export const SYSTEM_LANGUAGES = LANGUAGES;
export const LANGUAGE_STORAGE_KEY = "afrimed.lang";
export const LANGUAGE_CHANGE_EVENT = "afrimed-language-change";

/**
 * Normalize locale code into one of our configured language codes.
 */
export function normalizeLanguageCode(input) {
  const raw = String(input || "").trim();
  if (!raw) return "en-GB";

  const lower = raw.toLowerCase();

  // explicit aliases
  if (lower === "en" || lower === "en-gb" || lower === "en-uk") {
    return "en-GB";
  }

  const exact = LANGUAGES.find((l) => l.code.toLowerCase() === lower);
  if (exact) return exact.code;

  const short = lower.split("-")[0];
  if (short === "en") return "en-GB";

  const shortMatch = LANGUAGES.find(
    (l) => l.code.toLowerCase().split("-")[0] === short
  );
  if (shortMatch) return shortMatch.code;

  return "en-GB";
}

export function isActivatedAppLanguage(code) {
  const normalized = normalizeLanguageCode(code);
  return ACTIVATED_APP_LANGUAGE_CODES.includes(normalized);
}

export function normalizeAppLanguageCode(input) {
  const normalized = normalizeLanguageCode(input);
  const short = normalized.toLowerCase().split("-")[0];

  if (short === "en") return "en-GB";
  if (short === "fr") return "fr";
  if (short === "pt") return "pt";
  if (short === "de") return "de";
  if (short === "sn") return "sn";
  if (short === "zu") return "zu";

  if (isActivatedAppLanguage(normalized)) return normalized;
  return "en-GB";
}

export function getActivatedAppLanguages() {
  return ACTIVATED_APP_LANGUAGES.slice();
}

/**
 * Resolve the initial language code.
 * Priority:
 * 1) localStorage: afrimed.lang (if valid)
 * 2) browser language (full or short match)
 * 3) fallback "en-GB"
 */
export function getInitialLanguageCode() {
  if (typeof window === "undefined") return "en-GB";

  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      return normalizeAppLanguageCode(stored);
    }

    const navLang =
      window.navigator?.language || window.navigator?.userLanguage || "en-GB";

    return normalizeAppLanguageCode(navLang);
  } catch {
    return "en-GB";
  }
}

export function getLanguagePreference() {
  return getInitialLanguageCode();
}

export function getLanguageLabel(code) {
  const normalized = normalizeLanguageCode(code);
  const exact =
    ACTIVATED_APP_LANGUAGES.find((language) => language.code === normalized) ||
    LANGUAGES.find((language) => language.code === normalized);
  if (exact?.label) return exact.label;
  return "English";
}

export function getCurrentLanguageLabel() {
  return getLanguageLabel(getInitialLanguageCode());
}

function ensureMetaTag(selector, attributes = {}) {
  if (typeof document === "undefined") return null;
  let element = document.head?.querySelector(selector) || null;
  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      if (value != null) element.setAttribute(key, value);
    });
    document.head?.appendChild(element);
  }
  return element;
}

export function applyDocumentLanguage(code) {
  const normalized = normalizeAppLanguageCode(code);
  if (typeof document === "undefined") return normalized;

  const html = document.documentElement;
  if (html) {
    html.lang = normalized;
    html.setAttribute("lang", normalized);
    html.setAttribute("xml:lang", normalized);
  }

  const body = document.body;
  if (body) {
    body.setAttribute("lang", normalized);
  }

  const languageMeta = ensureMetaTag('meta[name="language"]', { name: "language" });
  if (languageMeta) languageMeta.setAttribute("content", normalized);

  const contentLanguageMeta = ensureMetaTag('meta[http-equiv="content-language"]', {
    "http-equiv": "content-language",
  });
  if (contentLanguageMeta) contentLanguageMeta.setAttribute("content", normalized);

  return normalized;
}

/**
 * Persist selected language and update <html lang="...">.
 */
export function setLanguagePreference(code) {
  const normalized = normalizeAppLanguageCode(code);
  if (typeof window === "undefined") return normalized;

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    applyDocumentLanguage(normalized);
    window.dispatchEvent(
      new CustomEvent(LANGUAGE_CHANGE_EVENT, {
        detail: { code: normalized },
      })
    );
  } catch {
    // no-op
  }

  return normalized;
}
